/**
 * Gamification domain event schemas.
 *
 * Typed payload validation for all gamification-related Mycelium events.
 * Rule: fields added after initial release MUST be `.optional()`.
 *
 * Multi-scope fields (organizationId, projectId) added for three-tier
 * gamification: Global → Organization → Project.
 *
 * @module gamification.events
 */

import { z } from 'zod'
import { BackboneEventSchema } from '../types/backbone.ts'

// ── Event schemas ───────────────────────────────────────────────────────

export const XpAwardedEvent = BackboneEventSchema.extend({
  domain: z.literal('gamification'),
  type: z.literal('xp_awarded'),
  payload: z.object({
    xp: z.number(),
    activityType: z.string(),
    source: z.string(),
    organizationId: z.string().optional(),
    projectId: z.string().optional(),
  }),
})

export const MilestoneAchievedEvent = BackboneEventSchema.extend({
  domain: z.literal('gamification'),
  type: z.literal('milestone_achieved'),
  payload: z.object({
    milestone: z.string(),
    totalXp: z.number(),
    triggeringActivity: z.string().optional(),
    source: z.string(),
    organizationId: z.string().optional(),
    projectId: z.string().optional(),
  }),
})

export const TeamChallengeCreatedEvent = BackboneEventSchema.extend({
  domain: z.literal('gamification'),
  type: z.literal('team_challenge_created'),
  payload: z.object({
    challengeId: z.string(),
    name: z.string(),
    description: z.string().optional(),
    targetXp: z.number(),
    durationMs: z.number(),
    source: z.string(),
    organizationId: z.string().optional(),
  }),
})

export const TeamXpAwardedEvent = BackboneEventSchema.extend({
  domain: z.literal('gamification'),
  type: z.literal('team_xp_awarded'),
  payload: z.object({
    individualXp: z.number(),
    teamXp: z.number(),
    multiplier: z.number(),
    teamTotal: z.number(),
    memberCount: z.number(),
    source: z.string(),
    organizationId: z.string().optional(),
    projectId: z.string().optional(),
  }),
})

export const TeamMilestoneBonusEvent = BackboneEventSchema.extend({
  domain: z.literal('gamification'),
  type: z.literal('team_milestone_bonus'),
  payload: z.object({
    bonusXp: z.number(),
    reason: z.string(),
    memberMilestone: z.string().optional(),
    teamTotal: z.number(),
    source: z.string(),
    organizationId: z.string().optional(),
  }),
})

export const TeamAchievementCascadeEvent = BackboneEventSchema.extend({
  domain: z.literal('gamification'),
  type: z.literal('team_achievement_cascade'),
  payload: z.object({
    individualAchievement: z.string(),
    teamId: z.string().optional(),
    source: z.string(),
    organizationId: z.string().optional(),
  }),
})

export const TeamMilestoneReachedEvent = BackboneEventSchema.extend({
  domain: z.literal('gamification'),
  type: z.literal('team_milestone_reached'),
  payload: z.object({
    milestone: z.string(),
    teamTotal: z.number(),
    memberCount: z.number(),
    source: z.string(),
    organizationId: z.string().optional(),
  }),
})

export const TeamChallengeCompletedEvent = BackboneEventSchema.extend({
  domain: z.literal('gamification'),
  type: z.literal('team_challenge_completed'),
  payload: z.object({
    challengeId: z.string(),
    challengeName: z.string(),
    teamScore: z.number(),
    targetXp: z.number(),
    source: z.string(),
    organizationId: z.string().optional(),
  }),
})

export const CommissionEarnedEvent = BackboneEventSchema.extend({
  domain: z.literal('gamification'),
  type: z.literal('commission_earned'),
  payload: z.object({
    commissionAmount: z.number(),
    commissionPercentage: z.number().optional(),
    role: z.string().optional(),
    invoiceId: z.string().optional(),
    clientId: z.string().optional(),
    sourceEventId: z.string().optional(),
    source: z.string().optional(),
    organizationId: z.string().optional(),
    projectId: z.string().optional(),
  }),
})

export const ProposalWonEvent = BackboneEventSchema.extend({
  domain: z.literal('gamification'),
  type: z.literal('proposal_won'),
  payload: z.object({
    proposalId: z.string().optional(),
    clientId: z.string().optional(),
    amount: z.number().optional(),
    source: z.string().optional(),
    organizationId: z.string().optional(),
    projectId: z.string().optional(),
  }),
})

export const PaymentCollectedEvent = BackboneEventSchema.extend({
  domain: z.literal('gamification'),
  type: z.literal('payment_collected'),
  payload: z.object({
    invoiceId: z.string().optional(),
    amount: z.number().optional(),
    clientId: z.string().optional(),
    source: z.string().optional(),
    organizationId: z.string().optional(),
    projectId: z.string().optional(),
  }),
})

// ── New multi-scope events ──────────────────────────────────────────────

export const OrgLeaderboardUpdatedEvent = BackboneEventSchema.extend({
  domain: z.literal('gamification'),
  type: z.literal('org_leaderboard_updated'),
  payload: z.object({
    organizationId: z.string(),
    topUserId: z.string().optional(),
    totalParticipants: z.number().optional(),
    source: z.string(),
  }),
})

export const ProjectMilestoneReachedEvent = BackboneEventSchema.extend({
  domain: z.literal('gamification'),
  type: z.literal('project_milestone_reached'),
  payload: z.object({
    projectId: z.string(),
    organizationId: z.string().optional(),
    milestone: z.string(),
    totalXp: z.number(),
    participantCount: z.number().optional(),
    source: z.string(),
  }),
})

// ── All gamification events ─────────────────────────────────────────────

export const GamificationDomainEvents = [
  XpAwardedEvent,
  MilestoneAchievedEvent,
  TeamChallengeCreatedEvent,
  TeamXpAwardedEvent,
  TeamMilestoneBonusEvent,
  TeamAchievementCascadeEvent,
  TeamMilestoneReachedEvent,
  TeamChallengeCompletedEvent,
  CommissionEarnedEvent,
  ProposalWonEvent,
  PaymentCollectedEvent,
  OrgLeaderboardUpdatedEvent,
  ProjectMilestoneReachedEvent,
] as const
